// Place all itemized pricing logic inside the QuotationPreparationModal component below
import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';
import {
  Calculator,
  Calendar as CalendarIcon,
  Clock,
  FileText,
  IndianRupee,
  User,
  AlertCircle,
  CheckCircle2,
  Send,
  Upload,
  X,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from '../hooks/use-toast';
import api from '../services/api';
import { useCalendarHolidays } from '../hooks/useHolidays';
import { QuotationType, QuotationStatus, DocumentTypeCode, DocumentReferenceType } from '../constants/stageConstants';

const QuotationPreparationModal = ({ open, onOpenChange, project, userRole, quotationType = QuotationType.BaseWork, onQuotationCreated, existingQuotation = null }) => {
  // quotationType can be 'base_work' or 'additional_work'
  // For additional_work, schedule tab is not needed
  // existingQuotation is provided when editing a draft quotation

  // Pricing State (removed pricePerSqft - now entity-wise)
  const [pricing, setPricing] = useState({
    extraWorkAmount: 0,
    discountType: 'percentage',
    discountValue: 0,
    gstPercentage: 18
  });

  // Itemized Quotation Items State
  const [quotationItems, setQuotationItems] = useState([]);

  // Calculated Amounts
  const [calculated, setCalculated] = useState({
    baseWorkAmount: 0,
    subtotal: 0,
    discountAmount: 0,
    taxableAmount: 0,
    gstAmount: 0,
    netAmount: 0
  });

  // Supervisor Schedule State
  const [schedule, setSchedule] = useState({
    proposedStartDate: null,
    supervisorId: '',
    slotId: ''
  });

  // Remarks
  const [remarks, setRemarks] = useState('');

  // Uploaded Documents (new documents to upload)
  const [uploadedDocuments, setUploadedDocuments] = useState([]);

  // Existing Documents (already uploaded to server)
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  // Document deletion confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);

  // Slot Availability State - Real Data
  const [slotAvailability, setSlotAvailability] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availableSupervisors, setAvailableSupervisors] = useState([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const isInitialScheduleLoad = useRef(false);

  // Calendar Month Availability
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [monthAvailability, setMonthAvailability] = useState({
    availableDates: [],
    unavailableDates: []
  });
  const [loadingMonthAvailability, setLoadingMonthAvailability] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { holidayDates, isHoliday, onMonthChange: onHolidayMonthChange } = useCalendarHolidays();

  // Submission State
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Active Tab
  const [activeTab, setActiveTab] = useState('pricing');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      // Reset all form state for new quotation
      setPricing({
        extraWorkAmount: 0,
        discountType: 'percentage',
        discountValue: 0,
        gstPercentage: 18
      });
      setCalculated({
        baseWorkAmount: 0,
        subtotal: 0,
        discountAmount: 0,
        taxableAmount: 0,
        gstAmount: 0,
        netAmount: 0
      });
      setSchedule({
        proposedStartDate: null,
        supervisorId: '',
        slotId: ''
      });
      setRemarks('');
      setExistingDocuments([]);
      setUploadedDocuments([]);
      setQuotationItems([]);
      setActiveTab('pricing');
    }
  }, [open]);

  // Pre-fill form when editing existing draft quotation or opening new
  useEffect(() => {
    if (open) {
      // Helper to normalize a project entity into a quotation item (handles different API shapes)
      const toQuotationItem = (entity) => ({
        ProjectEntityId: entity.id || entity.Id || entity.entityId || entity.EntityId,
        EntityName: entity.entityName || entity.EntityName || entity.name || entity.Name || 'Entity',
        UnitPrice: 0,
        Sqft: 0,
        TotalPrice: 0
      });

      // Resolve entities array from various possible field names
      const projectEntities = project
        ? (project.entities || project.Entities || project.projectEntities || project.ProjectEntities || [])
        : [];

      if (existingQuotation) {
        // Editing: load from quotation items (handle both camelCase and PascalCase)
        let items = existingQuotation.quotationItems || existingQuotation.QuotationItems || [];
        items = Array.isArray(items) ? items : [];

        if (items.length > 0) {
          // Has entity items — use them and ensure TotalPrice is calculated
          items = items.map(item => ({
            ProjectEntityId: item.projectEntityId || item.ProjectEntityId,
            EntityName: item.entityName || item.EntityName || 'Entity',
            UnitPrice: item.unitPrice ?? item.UnitPrice ?? 0,
            Sqft: item.sqft ?? item.Sqft ?? 0,
            TotalPrice: item.totalPrice || item.TotalPrice || ((item.unitPrice ?? item.UnitPrice ?? 0) * (item.sqft ?? item.Sqft ?? 0)) || 0
          }));
          setQuotationItems(items);
        } else {
          // No items — initialize from project entities with empty pricing
          setQuotationItems(Array.isArray(projectEntities) ? projectEntities.map(toQuotationItem) : []);
        }

        // Load other quotation fields (handle camelCase from API)
        const extraWork = existingQuotation.extraWorkAmount ?? existingQuotation.ExtraWorkAmount;
        const discType = existingQuotation.discountType || existingQuotation.DiscountType;
        const discValue = existingQuotation.discountValue ?? existingQuotation.DiscountValue;
        const gstPct = existingQuotation.gstPercentage ?? existingQuotation.GstPercentage;
        const notes = existingQuotation.notes || existingQuotation.Notes || existingQuotation.remarks || existingQuotation.Remarks;

        setPricing(prev => ({
          ...prev,
          extraWorkAmount: extraWork ?? prev.extraWorkAmount,
          discountType: discType || prev.discountType,
          discountValue: discValue ?? prev.discountValue,
          gstPercentage: gstPct ?? prev.gstPercentage
        }));

        if (notes) {
          setRemarks(notes);
        }

        // Load schedule data if available
        const proposedDate = existingQuotation.proposedStartDate || existingQuotation.ProposedStartDate;
        const supId = existingQuotation.supervisorId || existingQuotation.SupervisorId;
        const sId = existingQuotation.slotId || existingQuotation.SlotId;
        if (proposedDate || supId || sId) {
          isInitialScheduleLoad.current = true;
          setSchedule(prev => ({
            ...prev,
            proposedStartDate: proposedDate ? new Date(proposedDate) : prev.proposedStartDate,
            supervisorId: supId ? supId.toString() : prev.supervisorId,
            slotId: sId ? sId.toString() : prev.slotId
          }));
        }
      } else if (Array.isArray(projectEntities) && projectEntities.length > 0) {
        // Preparing new: load from project entities
        setQuotationItems(projectEntities.map(toQuotationItem));
      } else {
        setQuotationItems([]);
      }
    } else {
      setQuotationItems([]);
    }
  }, [open, existingQuotation, project]);

  // Fetch month availability when calendar month changes
  useEffect(() => {
    if (activeTab === 'schedule') {
      fetchMonthAvailability(calendarMonth);
    }
  }, [calendarMonth, activeTab]);

  // Fetch slot availability when date changes
  useEffect(() => {
    if (schedule.proposedStartDate) {
      fetchSlotAvailability(schedule.proposedStartDate);
      if (isInitialScheduleLoad.current) {
        // First load with existing data — don't reset, fetch supervisors for existing slot
        isInitialScheduleLoad.current = false;
        if (schedule.slotId) {
          fetchAvailableSupervisors(schedule.proposedStartDate, parseInt(schedule.slotId));
        }
      } else {
        // User changed date — reset selection
        setSchedule(prev => ({ ...prev, supervisorId: '', slotId: '' }));
        setSelectedSlot(null);
        setAvailableSupervisors([]);
      }
    }
  }, [schedule.proposedStartDate]);

  // Fetch month availability for calendar coloring
  const fetchMonthAvailability = async (month) => {
    try {
      setLoadingMonthAvailability(true);
      const year = month.getFullYear();
      const monthNum = month.getMonth() + 1; // JavaScript months are 0-indexed, API expects 1-indexed

      // Call the batch API - single request for entire month
      const data = await api.getMonthAvailability(year, monthNum);

      const available = [];
      const unavailable = [];

      // Process the response
      data.dates?.forEach(dateInfo => {
        const date = new Date(dateInfo.date);
        if (dateInfo.hasAvailability) {
          available.push(date);
        } else {
          unavailable.push(date);
        }
      });

      setMonthAvailability({ availableDates: available, unavailableDates: unavailable });
    } catch (error) {
      console.error('Error fetching month availability:', error);
      toast({
        title: 'Error',
        description: 'Failed to load calendar availability',
        variant: 'destructive'
      });
    } finally {
      setLoadingMonthAvailability(false);
    }
  };

  // Fetch slot availability from API
  const fetchSlotAvailability = async (date) => {
    try {
      setLoadingSlots(true);
      const projectId = project?.id || project?.Id;
      const data = await api.getSlotAvailability(date, projectId);
      setSlotAvailability(data.slots || []);
    } catch (error) {
      console.error('Error fetching slot availability:', error);
      toast({
        title: 'Error',
        description: 'Failed to load slot availability',
        variant: 'destructive'
      });
      setSlotAvailability([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Fetch available supervisors for selected slot
  const fetchAvailableSupervisors = async (date, slotId) => {
    try {
      setLoadingSupervisors(true);
      const projectId = project?.id || project?.Id;
      const data = await api.getAvailableSupervisors(date, slotId, projectId);
      setAvailableSupervisors(data.availableSupervisors || []);
      setSelectedSlot(data);
    } catch (error) {
      console.error('Error fetching available supervisors:', error);
      toast({
        title: 'Error',
        description: 'Failed to load available supervisors',
        variant: 'destructive'
      });
      setAvailableSupervisors([]);
    } finally {
      setLoadingSupervisors(false);
    }
  };

  // Handle slot selection
  const handleSlotSelect = (slot) => {
    if (slot.isAvailable && schedule.proposedStartDate) {
      setSchedule(prev => ({ ...prev, slotId: slot.slotId.toString(), supervisorId: '' }));
      fetchAvailableSupervisors(schedule.proposedStartDate, slot.slotId);
    }
  };

  // Calculate amounts from quotation items (entity-wise pricing)
  useEffect(() => {
    // Sum baseWorkAmount from all quotation items
    const baseWorkAmount = quotationItems.reduce((sum, item) => sum + (item.TotalPrice || 0), 0);
    const extraWorkAmount = parseFloat(pricing.extraWorkAmount) || 0;
    const discountValue = parseFloat(pricing.discountValue) || 0;
    const gstPercentage = pricing.gstPercentage !== '' && pricing.gstPercentage !== null && pricing.gstPercentage !== undefined
      ? parseFloat(pricing.gstPercentage)
      : 18;

    const subtotal = baseWorkAmount + extraWorkAmount;

    let discountAmount = 0;
    if (pricing.discountType === 'percentage') {
      discountAmount = (subtotal * discountValue) / 100;
    } else {
      discountAmount = discountValue;
    }

    const taxableAmount = subtotal - discountAmount;
    const gstAmount = (taxableAmount * gstPercentage) / 100;
    const netAmount = taxableAmount + gstAmount;

    setCalculated({
      baseWorkAmount: Math.round(baseWorkAmount * 100) / 100,
      subtotal: Math.round(subtotal * 100) / 100,
      discountAmount: Math.round(discountAmount * 100) / 100,
      taxableAmount: Math.round(taxableAmount * 100) / 100,
      gstAmount: Math.round(gstAmount * 100) / 100,
      netAmount: Math.round(netAmount * 100) / 100
    });
  }, [pricing, quotationItems]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get slot availability badge
  const getSlotBadge = (slot) => {
    if (!slot) return null;

    if (!slot.isAvailable || slot.availableSupervisors === 0) {
      return (
        <Badge variant="destructive" className="ml-2 shrink-0 whitespace-nowrap">
          0 available
        </Badge>
      );
    } else if (slot.availableSupervisors === 1) {
      return (
        <Badge className="ml-2 shrink-0 whitespace-nowrap bg-yellow-500 text-white hover:bg-yellow-600">
          1 available
        </Badge>
      );
    } else {
      return (
        <Badge className="ml-2 shrink-0 whitespace-nowrap bg-green-500 text-white hover:bg-green-600">
          {slot.availableSupervisors} available
        </Badge>
      );
    }
  };

  // Fetch existing documents for quotation
  const fetchExistingDocuments = async () => {
    if (!existingQuotation || !project) return;

    try {
      setLoadingDocuments(true);
      const quotationId = existingQuotation.id || existingQuotation.Id;

      console.log('Fetching documents for quotation:', quotationId);

      const response = await api.get(`/api/projects/${project.id || project.Id}/documents`, {
        params: {
          referenceType: 'quotation',
          referenceId: quotationId
        }
      });

      console.log('Existing documents fetched:', response.documents);
      setExistingDocuments(response.documents || []);
    } catch (error) {
      console.error('Failed to fetch existing documents:', error);
      toast({
        title: "Warning",
        description: "Failed to load existing documents",
        variant: "destructive"
      });
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Remove existing document (requires API call with confirmation)
  const confirmRemoveExistingDocument = (documentId, fileName) => {
    setDocumentToDelete({ id: documentId, fileName });
    setDeleteConfirmOpen(true);
  };

  const removeExistingDocument = async () => {
    if (!project || !documentToDelete) return;

    try {
      setDeletingDocId(documentToDelete.id);
      await api.delete(`/api/projects/${project.id || project.Id}/documents/${documentToDelete.id}`);
      setExistingDocuments(existingDocuments.filter(doc => doc.id !== documentToDelete.id));
      toast({
        title: "Success",
        description: "Document deleted successfully"
      });
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive"
      });
    } finally {
      setDeletingDocId(null);
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newDocuments = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      file: file
    }));
    setUploadedDocuments([...uploadedDocuments, ...newDocuments]);
  };

  // Remove uploaded document (new, not yet uploaded to server)
  const removeDocument = (docId) => {
    setUploadedDocuments(uploadedDocuments.filter(doc => doc.id !== docId));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Handle Share with OEM
  const handleShareWithOEM = async (status = QuotationStatus.Sent) => {
    // Validation: ensure at least one entity has pricing
    const hasValidPricing = quotationItems.some(item => item.UnitPrice > 0 && item.Sqft > 0);
    if (!hasValidPricing) {
      setActiveTab('pricing');
      toast({
        title: "Validation Error",
        description: "Please enter unit price and sqft for at least one entity.",
        variant: "destructive"
      });
      return;
    }

    // Only validate schedule for 'sent' status (not for draft)
    if (status === QuotationStatus.Sent && quotationType === QuotationType.BaseWork && (!schedule.proposedStartDate || !schedule.supervisorId || !schedule.slotId)) {
      setActiveTab('schedule');
      toast({
        title: "Validation Error",
        description: "Please select supervisor schedule and time slot.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSubmitting(true);
      console.log('=== CREATING QUOTATION ===');
      console.log('Project:', project);
      console.log('Schedule:', schedule);
      console.log('Status:', status);

      // Check if user is authenticated
      const token = localStorage.getItem('alignworks_token');
      console.log('Token exists:', !!token);
      if (token) {
        console.log('Token preview:', token.substring(0, 50) + '...');
      }

      // Prepare quotation data (entity-wise pricing - no pricePerSqft)
      const quotationData = {
        projectId: project.id || project.Id,
        quotationType: quotationType,
        status: status,
        // Pricing (NEW: entity-wise model)
        extraWorkAmount: parseFloat(pricing.extraWorkAmount) || 0,
        discountType: pricing.discountType,
        discountValue: parseFloat(pricing.discountValue) || 0,
        gstPercentage: pricing.gstPercentage !== '' && pricing.gstPercentage !== null && pricing.gstPercentage !== undefined ? parseFloat(pricing.gstPercentage) : 18,
        // Calculated amounts (backend will recalculate)
        baseWorkAmount: calculated.baseWorkAmount,
        subtotal: calculated.subtotal,
        discountAmount: calculated.discountAmount,
        taxableAmount: calculated.taxableAmount,
        gstAmount: calculated.gstAmount,
        netAmount: calculated.netAmount,
        // Schedule (only for base_work)
        proposedStartDate: schedule.proposedStartDate
          ? `${schedule.proposedStartDate.getFullYear()}-${String(schedule.proposedStartDate.getMonth() + 1).padStart(2, '0')}-${String(schedule.proposedStartDate.getDate()).padStart(2, '0')}`
          : null,
        supervisorId: schedule.supervisorId || null,
        slotId: schedule.slotId ? parseInt(schedule.slotId) : null,
        // Remarks
        remarks: remarks || null,
        // Itemized pricing (REQUIRED for new model)
        quotationItems: quotationItems.map(item => ({
          ProjectEntityId: item.ProjectEntityId,
          UnitPrice: parseFloat(item.UnitPrice) || 0,
          Sqft: parseFloat(item.Sqft) || 0,
          TotalPrice: parseFloat(item.TotalPrice) || 0
        }))
      };

      console.log('Quotation Data:', quotationData);
      console.log('Calling API...');

      // Call appropriate API based on whether editing or creating
      let response;
      if (existingQuotation) {
        console.log(`Updating existing quotation: ${existingQuotation.id}`);
        response = await api.updateQuotation(existingQuotation.id, quotationData);
        console.log('Update API Response:', response);
      } else {
        console.log('Creating new quotation');
        response = await api.createQuotation(quotationData);
        console.log('Create API Response:', response);
      }

      console.log('Quotation ID:', response.quotationId);

      // Upload documents if any
      if (uploadedDocuments.length > 0) {
        console.log(`Uploading ${uploadedDocuments.length} document(s) for quotation...`);
        console.log(`Quotation ID for reference: ${response.quotationId}`);
        let uploadSuccessCount = 0;
        let uploadFailCount = 0;

        for (const doc of uploadedDocuments) {
          try {
            const formData = new FormData();
            formData.append('file', doc.file);
            formData.append('documentTypeCode', DocumentTypeCode.QuotationPdf); // Now accepts PDFs, Word docs, and images
            formData.append('referenceType', DocumentReferenceType.Quotation);
            formData.append('referenceId', response.quotationId);

            console.log(`Uploading document: ${doc.name}, Reference ID: ${response.quotationId}`);
            await api.post(`/api/projects/${project.id || project.Id}/documents/upload`, formData);
            uploadSuccessCount++;
            console.log(`✓ Document uploaded successfully: ${doc.name}`);
          } catch (error) {
            console.error(`✗ Failed to upload document: ${doc.name}`, error);
            uploadFailCount++;
          }
        }

        console.log(`Upload summary: ${uploadSuccessCount} succeeded, ${uploadFailCount} failed`);

        if (uploadFailCount > 0) {
          toast({
            title: "Warning",
            description: `Quotation created but ${uploadFailCount} document(s) failed to upload.`,
            variant: "destructive"
          });
        } else if (uploadSuccessCount > 0) {
          toast({
            title: "Success",
            description: `Quotation created with ${uploadSuccessCount} document(s) uploaded.`,
            variant: "default"
          });
        }
      } else {
        toast({
          title: "Success",
          description: status === QuotationStatus.Draft
            ? "Quotation saved as draft successfully."
            : (response.message || "Quotation created and shared with OEM successfully."),
          variant: "default"
        });
      }

      // Call callback if provided
      if (onQuotationCreated) {
        onQuotationCreated(response);
      }

      // Close modal
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating quotation:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create quotation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!project) return null;

  const sqft = project.sqft || project.Sqft || 0;
  const clientName = project.clientName || project.ClientName || 'N/A';
  const siteName = project.siteName || project.SiteName || project.location || 'N/A';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[95vw] sm:max-w-5xl max-h-[85dvh] sm:max-h-[95vh] overflow-hidden p-0 flex flex-col gap-0 [&>button.absolute]:hidden">
          <DialogHeader className="shrink-0 bg-background border-b px-4 sm:px-6 pt-4 sm:pt-6 pb-3 pr-12">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="absolute right-3 top-3 sm:right-4 sm:top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none p-1"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <DialogTitle className="flex flex-wrap items-center gap-2 pr-8">
              <FileText className="h-5 w-5 shrink-0" />
              <span className="break-words">
                {existingQuotation
                  ? (existingQuotation.status?.toLowerCase() === QuotationStatus.Draft
                    ? 'Edit Draft Quotation'
                    : 'Edit Quotation')
                  : 'Prepare Quotation'}
              </span>
              <Badge variant={quotationType === QuotationType.BaseWork ? 'default' : 'secondary'}>
                {quotationType === QuotationType.BaseWork ? 'Base Work' : 'Additional Work'}
              </Badge>
              {existingQuotation && (
                <Badge
                  variant="outline"
                  className={
                    existingQuotation.status?.toLowerCase() === QuotationStatus.Draft
                      ? 'bg-orange-50 text-orange-700 border-orange-300'
                      : existingQuotation.status?.toLowerCase() === QuotationStatus.Sent
                        ? 'bg-blue-50 text-blue-700 border-blue-300'
                        : existingQuotation.status?.toLowerCase() === QuotationStatus.Approved
                          ? 'bg-green-50 text-green-700 border-green-300'
                          : 'bg-gray-50 text-gray-700 border-gray-300'
                  }
                >
                  {existingQuotation.status?.toLowerCase() === QuotationStatus.Draft && 'Draft'}
                  {existingQuotation.status?.toLowerCase() === QuotationStatus.Sent && 'Sent to OEM'}
                  {existingQuotation.status?.toLowerCase() === QuotationStatus.Approved && 'Approved'}
                  {existingQuotation.status?.toLowerCase() === QuotationStatus.Rejected && 'Rejected'}
                  {existingQuotation.status?.toLowerCase() === QuotationStatus.Expired && 'Expired'}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {clientName}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-6 px-4 sm:px-6 py-4">
            {/* Tabs for different sections */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className={cn("grid w-full", quotationType === QuotationType.BaseWork ? 'grid-cols-2' : 'grid-cols-1')}>
                <TabsTrigger value="pricing">
                  <Calculator className="h-4 w-4 mr-2" />
                  Pricing
                </TabsTrigger>
                {quotationType === QuotationType.BaseWork && (
                  <TabsTrigger value="schedule">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Schedule
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Pricing Tab */}
              <TabsContent value="pricing" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Pricing Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Itemized Quotation Items Section */}
                    <div className="mb-4">
                      <Label className="mb-2 block">Itemized Pricing (Entity-wise)</Label>
                      <table className="w-full text-sm border border-gray-200 mb-2">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="p-2 border">Entity Name</th>
                            <th className="p-2 border">Unit Price (₹)</th>
                            <th className="p-2 border">Sqft</th>
                            <th className="p-2 border">Total Price (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {quotationItems.map((item, idx) => (
                            <tr key={item.id || idx}>
                              <td className="border p-2">
                                <Input
                                  value={item.EntityName || ''}
                                  disabled
                                  placeholder="Entity Name"
                                />
                              </td>
                              <td className="border p-2">
                                <Input
                                  type="number"
                                  min="0"
                                  value={item.UnitPrice || ''}
                                  onChange={e => {
                                    const updated = [...quotationItems];
                                    updated[idx].UnitPrice = Math.max(0, parseFloat(e.target.value) || 0);
                                    updated[idx].TotalPrice = (updated[idx].UnitPrice || 0) * (updated[idx].Sqft || 0);
                                    setQuotationItems(updated);
                                  }}
                                  placeholder="0"
                                />
                              </td>
                              <td className="border p-2">
                                <Input
                                  type="number"
                                  min="0"
                                  value={item.Sqft || ''}
                                  onChange={e => {
                                    const updated = [...quotationItems];
                                    updated[idx].Sqft = Math.max(0, parseFloat(e.target.value) || 0);
                                    updated[idx].TotalPrice = (updated[idx].UnitPrice || 0) * (updated[idx].Sqft || 0);
                                    setQuotationItems(updated);
                                  }}
                                  placeholder="0"
                                />
                              </td>
                              <td className="border p-2">
                                <Input
                                  type="number"
                                  value={item.TotalPrice || 0}
                                  disabled
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <Label>Base Work Amount (Calculated from Entity Pricing)</Label>
                      <Input
                        value={formatCurrency(calculated.baseWorkAmount)}
                        disabled
                        className="bg-gray-50 mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Sum of entity-wise pricing: {quotationItems.length} entities
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="extraWorkAmount">Extra Work Amount</Label>
                        <div className="relative mt-1">
                          <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            id="extraWorkAmount"
                            type="number"
                            min="0"
                            placeholder="25000"
                            className="pl-9"
                            value={pricing.extraWorkAmount}
                            onChange={(e) => setPricing({ ...pricing, extraWorkAmount: Math.max(0, parseFloat(e.target.value) || 0) })}
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Subtotal (Calculated)</Label>
                        <Input
                          value={formatCurrency(calculated.subtotal)}
                          disabled
                          className="bg-gray-50 mt-1"
                        />
                      </div>
                    </div>

                    <Separator />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="discountType">Discount Type</Label>
                        <Select
                          value={pricing.discountType}
                          onValueChange={(value) => setPricing({ ...pricing, discountType: value })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Percentage (%)</SelectItem>
                            <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="discountValue">
                          Discount {pricing.discountType === 'percentage' ? '(%)' : '(₹)'}
                        </Label>
                        <Input
                          id="discountValue"
                          type="number"
                          min="0"
                          placeholder={pricing.discountType === 'percentage' ? '5' : '13750'}
                          className="mt-1"
                          value={pricing.discountValue}
                          onChange={(e) => setPricing({ ...pricing, discountValue: Math.max(0, parseFloat(e.target.value) || 0) })}
                        />
                      </div>
                      <div>
                        <Label>Discount Amount</Label>
                        <Input
                          value={formatCurrency(calculated.discountAmount)}
                          disabled
                          className="bg-gray-50 mt-1"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="gstPercentage">GST (%)</Label>
                        <Input
                          id="gstPercentage"
                          type="number"
                          min="0"
                          max="100"
                          placeholder="18"
                          className="mt-1"
                          value={pricing.gstPercentage}
                          onChange={(e) => setPricing({ ...pricing, gstPercentage: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                        />
                      </div>
                      <div>
                        <Label>GST Amount</Label>
                        <Input
                          value={formatCurrency(calculated.gstAmount)}
                          disabled
                          className="bg-gray-50 mt-1"
                        />
                      </div>
                    </div>

                    <Separator />

                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="pt-6">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Subtotal:</span>
                            <span className="font-medium">{formatCurrency(calculated.subtotal)}</span>
                          </div>
                          {calculated.discountAmount > 0 && (
                            <div className="flex justify-between text-sm text-red-600">
                              <span>Discount:</span>
                              <span className="font-medium">- {formatCurrency(calculated.discountAmount)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm">
                            <span>Taxable Amount:</span>
                            <span className="font-medium">{formatCurrency(calculated.taxableAmount)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>GST ({pricing.gstPercentage}%):</span>
                            <span className="font-medium">+ {formatCurrency(calculated.gstAmount)}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between text-lg font-bold">
                            <span>Net Amount:</span>
                            <span className="text-blue-700">{formatCurrency(calculated.netAmount)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Separator />

                    <div>
                      <Label htmlFor="remarks">Remarks / Notes</Label>
                      <Textarea
                        id="remarks"
                        placeholder="Any special notes or terms to include in the quotation..."
                        className="mt-1 min-h-[100px]"
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Upload Documents</CardTitle>
                    <CardDescription>Attach supporting documents for the quotation</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="document-upload" className="cursor-pointer">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition-all">
                          <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm font-medium text-gray-700">Click to upload documents</p>
                          <p className="text-xs text-gray-500 mt-1">PDF, Images, or Documents</p>
                        </div>
                      </Label>
                      <Input
                        id="document-upload"
                        type="file"
                        multiple
                        className="hidden"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={handleFileUpload}
                      />
                    </div>

                    {(existingDocuments.length > 0 || uploadedDocuments.length > 0) && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          Documents ({existingDocuments.length + uploadedDocuments.length})
                        </Label>

                        {/* Show existing documents first */}
                        {existingDocuments.map((doc) => (
                          <div key={`existing-${doc.id}`} className="flex items-center justify-between p-3 border rounded-lg bg-blue-50">
                            <div className="flex items-center gap-3 flex-1">
                              <FileText className="h-5 w-5 text-blue-600" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{doc.fileName}</p>
                                <p className="text-xs text-blue-600">Uploaded - {new Date(doc.uploadedAt).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => window.open(`/api/projects/${project.id || project.Id}/documents/${doc.id}/stream`, '_blank')}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                                title="View Document"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => confirmRemoveExistingDocument(doc.id, doc.fileName)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete Document"
                                disabled={deletingDocId !== null}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}

                        {/* Show newly uploaded documents */}
                        {uploadedDocuments.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                            <div className="flex items-center gap-3 flex-1">
                              <FileText className="h-5 w-5 text-gray-600" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
                                <p className="text-xs text-gray-500">{formatFileSize(doc.size)} - Pending upload</p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeDocument(doc.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent >

              {/* Schedule Tab */}
              < TabsContent value="schedule" className="space-y-4 mt-4" >
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Supervisor Schedule</CardTitle>
                    <CardDescription>
                      Step 1: Select date → Step 2: Choose available slot → Step 3: Select supervisor
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Step 1: Date Selection */}
                    <div>
                      <Label>Step 1: Select Proposed Start Date *</Label>
                      <Popover open={calendarOpen} onOpenChange={setCalendarOpen} modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCalendarOpen((v) => !v); }}
                            onPointerDown={(e) => e.stopPropagation()}
                            className={cn(
                              "w-full justify-start text-left font-normal mt-1 h-11",
                              !schedule.proposedStartDate && "text-gray-500"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {schedule.proposedStartDate
                              ? format(schedule.proposedStartDate, 'PPP')
                              : 'Select proposed start date'
                            }
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto p-0 z-[60]"
                          align="start"
                          side="bottom"
                          sideOffset={4}
                          avoidCollisions={true}
                          onOpenAutoFocus={(e) => e.preventDefault()}
                          onCloseAutoFocus={(e) => e.preventDefault()}
                        >
                          <Calendar
                            mode="single"
                            selected={schedule.proposedStartDate}
                            onSelect={(date) => {
                              setSchedule({ ...schedule, proposedStartDate: date });
                              setCalendarOpen(false);
                            }}
                            disabled={(date) => date < new Date() || isHoliday(date)}
                            month={calendarMonth}
                            onMonthChange={(month) => { setCalendarMonth(month); onHolidayMonthChange(month); }}
                            modifiers={{
                              available: monthAvailability.availableDates,
                              unavailable: monthAvailability.unavailableDates,
                              holiday: holidayDates
                            }}
                            modifiersClassNames={{
                              available: 'bg-green-100 text-green-900 hover:bg-green-200',
                              unavailable: 'bg-red-100 text-red-900 hover:bg-red-200',
                              holiday: 'holiday-day'
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Step 2: Slot Selection */}
                    {schedule.proposedStartDate && (
                      <div>
                        <Label>Step 2: Select Available Time Slot *</Label>
                        {loadingSlots ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                            <span className="ml-2 text-sm text-gray-600">Loading slot availability...</span>
                          </div>
                        ) : slotAvailability.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                            <p>No slots available for the selected date</p>
                          </div>
                        ) : (
                          <div className="space-y-2 mt-2">
                            {slotAvailability.map((slot) => (
                              <div
                                key={slot.slotId}
                                className={cn(
                                  "p-3 border-2 rounded-lg cursor-pointer transition-all flex items-center justify-between",
                                  schedule.slotId === slot.slotId.toString()
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-gray-300",
                                  !slot.isAvailable && "opacity-50 cursor-not-allowed"
                                )}
                                onClick={() => handleSlotSelect(slot)}
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <Clock className="h-4 w-4 text-gray-500 shrink-0" />
                                  <div className="min-w-0">
                                    <p className="font-medium text-sm break-words">{slot.slotName}</p>
                                    <p className="text-xs text-gray-500">
                                      {slot.startTime} - {slot.endTime}
                                    </p>
                                  </div>
                                </div>
                                {getSlotBadge(slot)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Step 3: Supervisor Selection */}
                    {schedule.slotId && (
                      <div>
                        <Label>Step 3: Select SuperAligner *</Label>
                        {loadingSupervisors ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                            <span className="ml-2 text-sm text-gray-600">Loading available supervisors...</span>
                          </div>
                        ) : availableSupervisors.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                            <p>No supervisors available for this slot</p>
                          </div>
                        ) : (
                          <div className="space-y-2 mt-2">
                            {availableSupervisors.map((supervisor) => (
                              <div
                                key={supervisor.supervisorId}
                                className={cn(
                                  "p-3 border-2 rounded-lg cursor-pointer transition-all",
                                  schedule.supervisorId === supervisor.supervisorId
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-gray-300"
                                )}
                                onClick={() => setSchedule({ ...schedule, supervisorId: supervisor.supervisorId })}
                              >
                                <div className="flex items-start gap-3">
                                  <User className="h-4 w-4 text-blue-600 shrink-0 mt-1" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm break-words">{supervisor.name}</p>
                                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                                      {supervisor.designation && <span className="break-words">📋 {supervisor.designation}</span>}
                                      {supervisor.skillLevel && <span>⭐ {supervisor.skillLevel}</span>}
                                      {supervisor.email && <span className="break-all">📧 {supervisor.email}</span>}
                                    </div>
                                  </div>
                                  {schedule.supervisorId === supervisor.supervisorId && (
                                    <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0" />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Confirmation Notice */}
                    {schedule.slotId && schedule.supervisorId && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
                        <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-yellow-800">Provisional Booking Notice</p>
                          <p className="text-yellow-700 mt-1">
                            When you share this quotation, the selected slot will be provisionally blocked for the chosen supervisor.
                            If the OEM approves, it becomes confirmed. If rejected, the slot will be released.
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent >
            </Tabs >
          </div >

          <DialogFooter className="shrink-0 gap-2 bg-background border-t px-4 sm:px-6 py-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            {/* Only show Save as Draft / Update Draft button if creating new OR editing a draft */}
            {(!existingQuotation || existingQuotation.status?.toLowerCase() === QuotationStatus.Draft) && (
              <Button
                variant="outline"
                onClick={() => handleShareWithOEM(QuotationStatus.Draft)}
                disabled={isSubmitting}
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    {existingQuotation ? 'Update Draft' : 'Save as Draft'}
                  </>
                )}
              </Button>
            )}
            <Button onClick={() => handleShareWithOEM(QuotationStatus.Sent)} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {existingQuotation ? 'Update & Send to OEM' : 'Share with OEM'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent >
      </Dialog >

      {/* Delete Document Confirmation Dialog */}
      < Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen} >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete <span className="font-semibold">{documentToDelete?.fileName}</span>?
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setDocumentToDelete(null);
                }}
                disabled={deletingDocId !== null}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={removeExistingDocument}
                disabled={deletingDocId !== null}
              >
                {deletingDocId ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog >
    </>
  );
};

export default QuotationPreparationModal;
